import { FC, useState, useEffect, useRef } from "react";
import { Button } from "@/components/ui/button";
import { PlusCircle, X, Globe, Server, Loader2, Pencil, Trash2 } from "lucide-react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { toast } from "sonner";

const formSchema = z.object({
  name: z.string().min(1, "Name is required"),
  url: z.string().url("Invalid URL").refine(
    (url) => url.startsWith("http://") || url.startsWith("https://"),
    "URL must start with http:// or https://"
  ),
  authToken: z.string().optional(),
});

type FormValues = z.infer<typeof formSchema>;

interface MCPConnection {
  id: string;
  name: string;
  url: string;
  authToken?: string;
}

const MCPConnectionsSettings: FC = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [connections, setConnections] = useState<MCPConnection[]>([]);
  const [testingConnections, setTestingConnections] = useState<Set<string>>(new Set());
  const [editingConnection, setEditingConnection] = useState<MCPConnection | null>(null);
  const modalRef = useRef<HTMLDivElement>(null);
  
  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      name: "",
      url: "",
      authToken: "",
    },
  });

  // Reset form when modal opens/closes or editing state changes
  useEffect(() => {
    if (!isModalOpen) {
      form.reset({
        name: "",
        url: "",
        authToken: "",
      });
      setEditingConnection(null);
    } else if (editingConnection) {
      form.reset({
        name: editingConnection.name,
        url: editingConnection.url,
        authToken: editingConnection.authToken,
      });
    }
  }, [isModalOpen, editingConnection, form]);

  const onSubmit = (data: FormValues) => {
    if (editingConnection) {
      setConnections((prev) =>
        prev.map((conn) =>
          conn.id === editingConnection.id
            ? { ...conn, ...data }
            : conn
        )
      );
      toast.success(`Updated ${data.name}`);
    } else {
      const newConnection: MCPConnection = {
        id: crypto.randomUUID(),
        name: data.name,
        url: data.url,
        authToken: data.authToken,
      };
      setConnections((prev) => [...prev, newConnection]);
      toast.success(`Added ${data.name}`);
    }
    
    setIsModalOpen(false);
  };

  const handleEdit = (connection: MCPConnection) => {
    setEditingConnection(connection);
    setIsModalOpen(true);
  };

  const handleDelete = (connection: MCPConnection) => {
    setConnections((prev) => prev.filter((conn) => conn.id !== connection.id));
    toast.success(`Removed ${connection.name}`);
  };

  const testConnection = async (connection: MCPConnection) => {
    setTestingConnections((prev) => new Set(prev).add(connection.id));
    
    try {
      const response = await fetch(`${connection.url}/status`, {
        method: 'GET',
        headers: connection.authToken ? {
          'Authorization': `Bearer ${connection.authToken}`
        } : undefined
      });

      if (!response.ok) {
        throw new Error(`Server returned ${response.status}`);
      }

      toast.success(`Successfully connected to ${connection.name}`);
    } catch (error) {
      toast.error(`Failed to connect to ${connection.name}: ${error instanceof Error ? error.message : 'Unknown error'}`);
    } finally {
      setTestingConnections((prev) => {
        const next = new Set(prev);
        next.delete(connection.id);
        return next;
      });
    }
  };

  // Handle click outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (modalRef.current && !modalRef.current.contains(event.target as Node)) {
        setIsModalOpen(false);
      }
    };

    // Handle escape key
    const handleEscapeKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setIsModalOpen(false);
      }
    };

    if (isModalOpen) {
      document.addEventListener("mousedown", handleClickOutside);
      document.addEventListener("keydown", handleEscapeKey);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("keydown", handleEscapeKey);
    };
  }, [isModalOpen]);

  return (
    <div className="container mx-auto px-4 sm:px-6 py-6 max-w-7xl">
      <div className="space-y-6">
        <div className="space-y-2">
          <h1 className="text-2xl font-semibold tracking-tight">MCP Server Connections</h1>
          <p className="text-sm text-muted-foreground">
            Manage model context protocol (MCP) servers for your organization.
          </p>
        </div>

        {connections.length === 0 ? (
          <div className="rounded-lg border bg-card text-card-foreground shadow-sm">
            <div className="p-4 sm:p-6 flex flex-col items-center justify-center space-y-4 text-center">
              <div className="space-y-2">
                <h3 className="text-lg font-medium">No MCP connections yet</h3>
                <p className="text-sm text-muted-foreground">
                  Add your first MCP server connection to get started with model context management.
                </p>
              </div>
              <Button onClick={() => setIsModalOpen(true)} className="w-full sm:w-auto">
                <PlusCircle className="mr-2 h-4 w-4" />
                Add Connection
              </Button>
            </div>
          </div>
        ) : (
          <div className="space-y-4">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
              <h2 className="text-lg font-medium">Active Connections</h2>
              <Button onClick={() => setIsModalOpen(true)} className="w-full sm:w-auto">
                <PlusCircle className="mr-2 h-4 w-4" />
                Add Connection
              </Button>
            </div>
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {connections.map((connection) => (
                <div
                  key={connection.id}
                  className="rounded-lg border bg-card p-4 hover:shadow-sm transition-shadow group min-w-0"
                >
                  <div className="flex items-start space-x-3">
                    <div className="p-2 bg-primary/10 rounded-md shrink-0">
                      <Server className="h-5 w-5 text-primary" />
                    </div>
                    <div className="flex-1 min-w-0 space-y-1">
                      <div className="flex items-start justify-between gap-2">
                        <h3 className="font-medium truncate">{connection.name}</h3>
                        <div className="flex items-center space-x-1 opacity-0 group-hover:opacity-100 transition-opacity shrink-0">
                          <button
                            onClick={() => handleEdit(connection)}
                            className="p-1 hover:bg-gray-100 rounded-md transition-colors"
                          >
                            <Pencil className="h-4 w-4 text-gray-500" />
                          </button>
                          <button
                            onClick={() => handleDelete(connection)}
                            className="p-1 hover:bg-gray-100 rounded-md transition-colors"
                          >
                            <Trash2 className="h-4 w-4 text-gray-500" />
                          </button>
                        </div>
                      </div>
                      <div className="flex items-center text-sm text-muted-foreground">
                        <Globe className="h-4 w-4 mr-1 shrink-0" />
                        <span className="truncate">{connection.url}</span>
                      </div>
                      <div className="pt-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => testConnection(connection)}
                          disabled={testingConnections.has(connection.id)}
                          className="w-full"
                        >
                          {testingConnections.has(connection.id) ? (
                            <>
                              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                              Testing...
                            </>
                          ) : (
                            "Test Connection"
                          )}
                        </Button>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {isModalOpen && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div
              ref={modalRef}
              className="bg-white rounded-lg shadow-lg w-full max-w-md mx-auto"
            >
              <div className="flex items-center justify-between p-4 sm:p-6 border-b">
                <h2 className="text-lg font-semibold">
                  {editingConnection ? "Edit Connection" : "Add New MCP Server"}
                </h2>
                <button
                  onClick={() => setIsModalOpen(false)}
                  className="text-gray-500 hover:text-gray-700"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>
              <div className="p-4 sm:p-6">
                <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
                  <div className="space-y-2">
                    <label htmlFor="name" className="text-sm font-medium">
                      Name
                    </label>
                    <input
                      id="name"
                      type="text"
                      className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                      placeholder="My MCP Server"
                      {...form.register("name")}
                    />
                    {form.formState.errors.name && (
                      <p className="text-sm text-red-500">{form.formState.errors.name.message}</p>
                    )}
                  </div>

                  <div className="space-y-2">
                    <label htmlFor="url" className="text-sm font-medium">
                      URL
                    </label>
                    <input
                      id="url"
                      type="text"
                      className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                      placeholder="https://mcp-server.example.com"
                      {...form.register("url")}
                    />
                    {form.formState.errors.url && (
                      <p className="text-sm text-red-500">{form.formState.errors.url.message}</p>
                    )}
                  </div>

                  <div className="space-y-2">
                    <label htmlFor="authToken" className="text-sm font-medium">
                      Auth Token (Optional)
                    </label>
                    <input
                      id="authToken"
                      type="password"
                      className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                      placeholder="Enter auth token"
                      {...form.register("authToken")}
                    />
                    {form.formState.errors.authToken && (
                      <p className="text-sm text-red-500">{form.formState.errors.authToken.message}</p>
                    )}
                  </div>

                  <div className="flex flex-col sm:flex-row justify-end gap-2 pt-4">
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => setIsModalOpen(false)}
                      className="w-full sm:w-auto"
                    >
                      Cancel
                    </Button>
                    <Button type="submit" className="w-full sm:w-auto">
                      {editingConnection ? "Save Changes" : "Save Connection"}
                    </Button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default MCPConnectionsSettings; 